import { supabase } from '../lib/supabase';
import { compressImage, needsCompression, getOptimalFormat, detectImageFormat } from '../utils/imageOptimizer';

export const uploadService = {
  /**
   * 上传图片到 Supabase Storage（带压缩优化）
   * @param file 要上传的文件
   * @param bucket Storage bucket 名称
   * @param compress 是否压缩图片
   * @returns 图片的公开 URL
   */
  async uploadImage(
    file: File, 
    bucket: string = 'images',
    compress: boolean = true
  ): Promise<string> {
    // 检查 Supabase 是否已配置
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey || 
        supabaseUrl === 'YOUR_SUPABASE_URL' || 
        supabaseKey === 'YOUR_SUPABASE_ANON_KEY') {
      console.warn('Supabase Storage 未配置，使用占位图片');
      return this.generatePlaceholder(file.name);
    }

    try {
      let fileToUpload: File | Blob = file;
      let fileExt = file.name.split('.').pop() || 'jpg';

      // 如果需要压缩且文件较大
      if (compress && needsCompression(file, 500 * 1024)) { // 500KB
        console.log(`压缩图片: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
        
        const originalFormat = detectImageFormat(file);
        const optimalFormat = getOptimalFormat(originalFormat);
        
        // 压缩图片
        const compressedBlob = await compressImage(file, {
          maxWidth: 1200,
          maxHeight: 1200,
          quality: 0.85,
          format: optimalFormat
        });

        fileToUpload = compressedBlob;
        fileExt = optimalFormat;
        
        console.log(`压缩完成: ${(compressedBlob.size / 1024).toFixed(2)} KB (节省 ${((1 - compressedBlob.size / file.size) * 100).toFixed(1)}%)`);
      }

      // 生成唯一的文件名
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = fileName;

      // 上传文件到 Supabase Storage
      const { error } = await supabase.storage
        .from(bucket)
        .upload(filePath, fileToUpload, {
          cacheControl: '31536000', // 1年缓存
          upsert: false,
          contentType: `image/${fileExt}`
        });

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }

      // 获取公开 URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      // 如果上传失败，返回占位图片
      return this.generatePlaceholder(file.name);
    }
  },

  /**
   * 批量上传图片
   * @param files 文件数组
   * @param bucket Storage bucket 名称
   * @param compress 是否压缩
   * @returns 图片 URL 数组
   */
  async uploadImages(
    files: File[],
    bucket: string = 'images',
    compress: boolean = true
  ): Promise<string[]> {
    const uploadPromises = files.map(file => 
      this.uploadImage(file, bucket, compress)
    );
    return Promise.all(uploadPromises);
  },

  /**
   * 删除图片
   * @param url 图片 URL
   * @param bucket Storage bucket 名称
   */
  async deleteImage(url: string, bucket: string = 'images'): Promise<void> {
    try {
      // 跳过占位图片
      if (url.includes('placeholder') || url.includes('data:image')) {
        return;
      }

      // 从 URL 中提取文件路径
      const urlParts = url.split('/');
      const filePath = urlParts[urlParts.length - 1];

      const { error } = await supabase.storage
        .from(bucket)
        .remove([filePath]);

      if (error) {
        console.error('Delete error:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  },

  /**
   * 批量删除图片
   * @param urls 图片 URL 数组
   * @param bucket Storage bucket 名称
   */
  async deleteImages(urls: string[], bucket: string = 'images'): Promise<void> {
    const deletePromises = urls.map(url => 
      this.deleteImage(url, bucket)
    );
    await Promise.all(deletePromises);
  },

  /**
   * 生成占位图片（动漫风格）
   * @param fileName 文件名
   * @returns SVG 占位图片 URL
   */
  generatePlaceholder(fileName: string): string {
    const shortName = fileName.substring(0, 10);
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23ffeef8;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23fff5f7;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23grad)' width='300' height='300'/%3E%3Ctext x='50%25' y='45%25' dominant-baseline='middle' text-anchor='middle' fill='%23FF6B9D' font-size='16' font-weight='700'%3E${encodeURIComponent(shortName)}%3C/text%3E%3Ctext x='50%25' y='55%25' dominant-baseline='middle' text-anchor='middle' fill='%23FFB6C1' font-size='12' font-weight='600'%3E占位图片%3C/text%3E%3C/svg%3E`;
  },

  /**
   * 获取优化后的图片 URL
   * @param url 原始 URL
   * @param width 宽度
   * @param height 高度
   * @param quality 质量
   * @returns 优化后的 URL
   */
  getOptimizedUrl(
    url: string,
    width?: number,
    height?: number,
    quality: number = 80
  ): string {
    // 如果是 Supabase URL，添加转换参数
    if (url.includes('supabase')) {
      try {
        const urlObj = new URL(url);
        if (width) urlObj.searchParams.set('width', width.toString());
        if (height) urlObj.searchParams.set('height', height.toString());
        urlObj.searchParams.set('quality', quality.toString());
        urlObj.searchParams.set('format', 'webp');
        return urlObj.toString();
      } catch (e) {
        console.error('Invalid URL:', e);
        return url;
      }
    }
    return url;
  }
};

